import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { dataService } from '@/lib/dataService'
import { dashboardService } from '@/lib/dashboardService'
import { useAuth } from '@/contexts/AuthContext'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'

export const DepartmentReports: React.FC = () => {
  const { user, role } = useAuth()
  const [selectedDeptId, setSelectedDeptId] = useState<string>('')
  const [allDepts, setAllDepts] = useState<any[]>([])
  const [department, setDepartment] = useState<any>(null)
  const [groupedReports, setGroupedReports] = useState<Record<string, { subjectCode: string; subjectName: string; classes: any[] }>>({})
  const [loading, setLoading] = useState(true)

  const loadReportData = async () => {
    if (!user) return
    setLoading(true)
    try {
      try {
        await dashboardService.syncEnrollments()
      } catch (syncErr) {
        console.warn('Sync enrollments failed silently:', syncErr)
      }
      const depts = await dataService.getDepartments()
      setAllDepts(depts)

      let deptId: number | null = null
      const allProfiles = await dataService.getProfiles()
      
      if (role === 'admin') {
        if (selectedDeptId) {
          deptId = Number(selectedDeptId)
        } else if (depts.length > 0) {
          deptId = depts[0].id
          setSelectedDeptId(String(depts[0].id))
        }
      } else {
        const currentProfile = allProfiles.find((p: any) => p.id === user.id)
        deptId = currentProfile?.department_id || null
      }

      if (deptId !== null) {
        const allSessions = await dataService.getAssessmentSessions()
        const activeSession = allSessions.find((s: any) => s.is_open)
        const sessionId = activeSession?.id || 1

        const summary = await dataService.getDepartmentSummary(deptId, sessionId)
        const currentDept = depts.find((d: any) => d.id === deptId)
        setDepartment(currentDept)

        const processedReports = summary.map((s: any) => {
          const maxScore = Math.max(s.maxPreScore, s.maxPostScore) || 10
          const prePercentage = Math.round((s.preAvg / maxScore) * 100)
          const postPercentage = Math.round((s.postAvg / maxScore) * 100)

          return {
            id: s.assignmentId,
            subjectId: s.subjectId,
            subjectCode: s.subjectCode,
            subjectName: s.subjectName,
            classroomName: s.classroomName,
            studentsCount: s.totalStudents,
            preAvg: s.preAvg.toFixed(2),
            postAvg: s.postAvg.toFixed(2),
            learningGain: s.learningGain.toFixed(2),
            prePercentage,
            postPercentage,
            maxScore
          }
        })

        // Group by subject for cross-classroom comparisons
        const groups: Record<string, { subjectCode: string; subjectName: string; classes: any[] }> = {}
        processedReports.forEach((rep: any) => {
          const key = rep.subjectCode
          if (!groups[key]) {
            groups[key] = {
              subjectCode: rep.subjectCode,
              subjectName: rep.subjectName,
              classes: []
            }
          }
          groups[key].classes.push(rep)
        })

        setGroupedReports(groups)
      }
    } catch (err) {
      console.error('Error loading HOD reports:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReportData()
  }, [user, selectedDeptId])

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  const subjectKeys = Object.keys(groupedReports)

  if (!department && role !== 'admin') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <div>
          <Link to="/department" style={{ fontSize: '13px', color: 'var(--primary)', display: 'inline-block', marginBottom: '8px', textDecoration: 'none' }}>← กลับไปแดชบอร์ดติดตาม</Link>
          <h1 style={{ fontSize: '28px', fontWeight: '700' }}>📈 รายงานวิเคราะห์ผลสัมฤทธิ์กลุ่มสาระฯ</h1>
        </div>
        <div className="glassmorphism" style={{ padding: '40px', textAlign: 'center' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '20px' }}>⚠️</span>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>ไม่สามารถดึงข้อมูลกลุ่มสาระฯ ได้</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '500px', margin: '0 auto 24px' }}>
            เนื่องจากบัญชีของคุณยังไม่ได้เชื่อมโยงกับกลุ่มสาระวิชาใด ๆ ในระบบ กรุณาอัปเดตกลุ่มสาระของคุณที่หน้าข้อมูลส่วนตัว หรือติดต่อผู้ดูแลระบบเพื่อแก้ไขสิทธิ์
          </p>
          <Link to="/department/profile" className="btn btn-primary">👤 ตั้งค่ากลุ่มสาระในหน้าข้อมูลส่วนตัว</Link>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      
      {/* Department Selector for Admin */}
      {role === 'admin' && allDepts.length > 0 && (
        <div className="glassmorphism" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)' }}>
            📂 สลับกลุ่มสาระการเรียนรู้:
          </label>
          <select
            value={selectedDeptId}
            onChange={(e) => setSelectedDeptId(e.target.value)}
            className="input-field"
            style={{ width: '280px', margin: 0 }}
          >
            {allDepts.map((d: any) => (
              <option key={d.id} value={String(d.id)}>{d.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Page Header */}
      <div>
        <Link to="/department" style={{ fontSize: '13px', color: 'var(--primary)', display: 'inline-block', marginBottom: '8px', textDecoration: 'none' }}>← กลับไปแดชบอร์ดติดตาม</Link>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>📈 รายงานวิเคราะห์ผลสัมฤทธิ์กลุ่มสาระฯ (HOD Reports)</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          กลุ่มสาระการเรียนรู้: <strong>{department?.name}</strong> | ระบบเปรียบเทียบสถิติระหว่างห้องเรียน (Cross-classroom Learning Gain Comparison)
        </p>
      </div>

      {/* Main Reports Visualizer List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
        
        {subjectKeys.length === 0 ? (
          <div className="glassmorphism" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            📭 ไม่พบข้อมูลตารางการกรอกคะแนนในขณะนี้
          </div>
        ) : (
          subjectKeys.map((key) => {
            const group = groupedReports[key]
            return (
              <div key={key} className="glassmorphism" style={{ padding: '28px' }}>
                
                {/* Subject Header */}
                <div style={{ marginBottom: '24px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--primary)', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                    รหัสรายวิชา: {group.subjectCode}
                  </span>
                  <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    รายวิชา{group.subjectName}
                  </h2>
                </div>

                {/* SVG Comparison Chart: comparing classrooms Learning Gain */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.4)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '28px'
                }}>
                  <span style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                    📊 เปรียบเทียบพัฒนาการการเรียนรู้ (Average Learning Gain) ของแต่ละห้องเรียน
                  </span>

                  <div style={{ height: '240px', width: '100%', marginTop: '10px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={group.classes}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="classroomName" stroke="var(--text-secondary)" fontSize={11} tickLine={false} />
                        <YAxis stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip 
                          contentStyle={{ 
                            background: 'var(--bg-card)', 
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            color: 'var(--text-primary)' 
                          }} 
                        />
                        <Bar name="Average Learning Gain" dataKey="learningGain" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Comparison Table */}
                <div className="custom-table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>ห้องเรียน</th>
                        <th style={{ textAlign: 'center' }}>จำนวนเด็กปกติ</th>
                        <th style={{ textAlign: 'center' }}>เฉลี่ย Pre-Test</th>
                        <th style={{ textAlign: 'center' }}>เฉลี่ย Post-Test</th>
                        <th style={{ textAlign: 'center' }}>คะแนนเต็ม</th>
                        <th style={{ textAlign: 'center', color: 'var(--success)' }}>พัฒนาการเฉลี่ย (Learning Gain)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.classes.map((cls) => (
                        <tr key={cls.id}>
                          <td><strong>{cls.classroomName}</strong></td>
                          <td style={{ textAlign: 'center' }}>{cls.studentsCount} คน</td>
                          <td style={{ textAlign: 'center', fontWeight: '500' }}>{cls.preAvg} ({cls.prePercentage}%)</td>
                          <td style={{ textAlign: 'center', fontWeight: '500' }}>{cls.postAvg} ({cls.postPercentage}%)</td>
                          <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{cls.maxScore} คะแนน</td>
                          <td style={{ textAlign: 'center', fontWeight: '800', color: 'var(--success)', fontSize: '15px' }}>
                            +{cls.learningGain}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </div>
            )
          })
        )}

      </div>

    </div>
  )
}
export default DepartmentReports
